/**
 * Offline retry queue for FIELD SALES (batch #3 — offline sales sync).
 *
 * A field rep can record a sale + new-signup contact while OFFLINE. The capture is
 * facts-only (rep position + signup name/email/phone + optional org/event/notes); on
 * sync we call `record_offline_field_sale`, which server-side resolves-or-creates the
 * contact from the email and records the phone sale, atomically + IDEMPOTENTLY by
 * (created_by, client_id). `client_id` is generated ONCE at capture and reused on every
 * retry, so re-draining never double-creates a contact or a sale.
 *
 * Sibling of eventScanQueue / redemptionQueue (own localStorage key + bounds):
 * 24h TTL, 5 network retries, cap 50.
 *
 * Failure classification: the RPC THROWS on both business and network failures. A business
 * failure (RAISE / constraint) surfaces as a PostgREST error WITH a `.code` (e.g. 'P0001'
 * for RAISE, '23xxx' for a constraint) → definitive, do NOT retry (UI offers dismiss). A
 * network failure has no `.code` ("Failed to fetch") → retryable.
 */
import {
  recordOfflineFieldSale,
  type RecordOfflineFieldSaleInput,
} from "./salesActivities.service";
import { isTransientError } from "../utils/transientError";
import type { UUID } from "../types/common.types";

const STORAGE_KEY = "field_sale_retry_queue_v1";
const TTL_MS = 24 * 60 * 60 * 1000;
const MAX_ATTEMPTS = 5;
const QUEUE_CAP = 50;

export type FieldSaleQueueStatus = "pending" | "retrying" | "failed";

export interface QueuedFieldSale {
  id: string;
  client_id: UUID; // idempotency key (server-side dedup)
  rep_position_id: UUID;
  signup: {
    firstName?: string | null;
    lastName?: string | null;
    email: string;
    phone?: string | null;
    zip?: string | null;
  };
  organization_id?: UUID | null;
  event_id?: UUID | null;
  notes?: string | null;
  payload?: unknown;
  label: string; // for the banner (name or email)
  enqueuedAt: number;
  lastAttemptAt: number | null;
  attemptCount: number;
  status: FieldSaleQueueStatus;
  lastError?: string;
  // false = a definitive server verdict (auth/validation/already-logged) that re-running
  // won't fix → UI offers dismiss, not retry. true/undefined = a network failure.
  retryable?: boolean;
}

export interface FieldSaleProcessOutcome {
  processed: number;
  succeeded: number;
  businessFailures: number;
  networkFailures: number;
  failed: number;
  remaining: number;
}

function genId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function readRaw(): QueuedFieldSale[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as QueuedFieldSale[]) : [];
  } catch {
    return [];
  }
}

function writeRaw(entries: QueuedFieldSale[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch (err) {
    console.warn("Failed to persist field-sale queue:", err);
  }
}

function pruneExpired(entries: QueuedFieldSale[]): QueuedFieldSale[] {
  const cutoff = Date.now() - TTL_MS;
  return entries.filter((e) => e.enqueuedAt >= cutoff);
}

export function getQueue(): QueuedFieldSale[] {
  const pruned = pruneExpired(readRaw());
  writeRaw(pruned);
  return pruned;
}

export interface EnqueueFieldSaleInput {
  repPositionId: UUID;
  signup: {
    firstName?: string | null;
    lastName?: string | null;
    email: string;
    phone?: string | null;
    zip?: string | null;
  };
  organizationId?: UUID | null;
  eventId?: UUID | null;
  notes?: string | null;
  payload?: unknown;
}

/**
 * Add a field sale to the queue. Generates the `client_id` idempotency key here (once),
 * so any later sync retry is safe. Returns the entry, or null if the queue is at cap.
 */
export function enqueue(input: EnqueueFieldSaleInput): QueuedFieldSale | null {
  const entries = pruneExpired(readRaw());
  if (entries.length >= QUEUE_CAP) {
    return null;
  }
  const name = [input.signup.firstName, input.signup.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  const entry: QueuedFieldSale = {
    id: genId(),
    client_id: genId() as UUID,
    rep_position_id: input.repPositionId,
    signup: input.signup,
    organization_id: input.organizationId ?? null,
    event_id: input.eventId ?? null,
    notes: input.notes ?? null,
    payload: input.payload ?? null,
    label: name || input.signup.email,
    enqueuedAt: Date.now(),
    lastAttemptAt: null,
    attemptCount: 0,
    status: "pending",
  };
  entries.push(entry);
  writeRaw(entries);
  return entry;
}

export function removeEntry(id: string): void {
  writeRaw(readRaw().filter((e) => e.id !== id));
}

/** Re-arm a failed (definitive) entry for another manual attempt. */
export function retryEntry(id: string): void {
  writeRaw(
    readRaw().map((e) =>
      e.id === id
        ? {
            ...e,
            status: "pending" as FieldSaleQueueStatus,
            retryable: undefined,
          }
        : e,
    ),
  );
}

export interface FieldSaleProcessHooks {
  onSuccess?: (entry: QueuedFieldSale, saleId: string) => void;
  onBusinessFailure?: (entry: QueuedFieldSale, message: string) => void;
}

/**
 * True when an error is a DEFINITIVE server verdict (auth/validation/business/constraint)
 * that won't change on retry. Delegates to the shared transient classifier so all callers
 * agree on what's retryable (network errors, serialization/deadlock/lock/timeout, and the
 * resource/connection classes are transient; everything else is definitive).
 */
function isDefinitive(err: unknown): boolean {
  return !isTransientError(err);
}

/** Process the queue once, sequentially. */
export async function processQueue(
  hooks: FieldSaleProcessHooks = {},
): Promise<FieldSaleProcessOutcome> {
  const outcome: FieldSaleProcessOutcome = {
    processed: 0,
    succeeded: 0,
    businessFailures: 0,
    networkFailures: 0,
    failed: 0,
    remaining: 0,
  };

  const pending = pruneExpired(readRaw()).filter(
    (e) => e.status === "pending" || e.status === "retrying",
  );

  for (const entry of pending) {
    outcome.processed++;
    writeRaw(
      readRaw().map((e) =>
        e.id === entry.id
          ? { ...e, status: "retrying" as FieldSaleQueueStatus }
          : e,
      ),
    );

    try {
      const rpcInput: RecordOfflineFieldSaleInput = {
        repPositionId: entry.rep_position_id,
        signup: entry.signup,
        organizationId: entry.organization_id,
        eventId: entry.event_id,
        clientId: entry.client_id,
        notes: entry.notes,
        payload: entry.payload,
      };
      const saleId = await recordOfflineFieldSale(rpcInput);
      outcome.succeeded++;
      hooks.onSuccess?.(entry, saleId);
      writeRaw(readRaw().filter((e) => e.id !== entry.id));
      continue;
    } catch (err) {
      const now = Date.now();
      const message =
        (err as { message?: string } | null)?.message ?? "Sync error";
      if (isDefinitive(err)) {
        // Auth/validation/already-logged → won't fix on retry.
        outcome.businessFailures++;
        hooks.onBusinessFailure?.(entry, message);
        writeRaw(
          readRaw().map((e) =>
            e.id === entry.id
              ? {
                  ...e,
                  status: "failed" as FieldSaleQueueStatus,
                  lastAttemptAt: now,
                  lastError: message,
                  retryable: false,
                }
              : e,
          ),
        );
        continue;
      }
      // Network/transient → retry up to MAX_ATTEMPTS.
      outcome.networkFailures++;
      const newAttempt = entry.attemptCount + 1;
      const exhausted = newAttempt >= MAX_ATTEMPTS;
      if (exhausted) {
        outcome.failed++;
      }
      writeRaw(
        readRaw().map((e) =>
          e.id === entry.id
            ? {
                ...e,
                status: (exhausted
                  ? "failed"
                  : "pending") as FieldSaleQueueStatus,
                attemptCount: newAttempt,
                lastAttemptAt: now,
                lastError: message,
                retryable: true,
              }
            : e,
        ),
      );
    }
  }

  outcome.remaining = readRaw().length;
  return outcome;
}

export const FIELD_SALE_QUEUE_BOUNDS = { TTL_MS, MAX_ATTEMPTS, QUEUE_CAP };
